// [SNOWPACKS]: one row per sub-area kind present (PLOWABLE/IMPERVIOUS/
// PERVIOUS) plus an optional REMOVAL row.

import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export interface SnowSubArea {
  cmin: number;
  cmax: number;
  tbase: number;
  fwFrac: number;
  sd0: number;
  fw0: number;
  /** SNN0 for Plowable/Impervious, SD100 for Pervious — same column, different meaning per sub-area kind, same as the toolkit's own single `x[6]`. */
  snn0OrSd100: number;
}

export interface SnowRemoval {
  sdPlow: number;
  fout: number;
  fimperv: number;
  fperv: number;
  fimelt: number;
  fsubcatch: number;
  /** Subcatchment receiving the plowed snow, or "" for none (the toolkit's own -1 sentinel). */
  toSubcatch: string;
}

export interface SnowPack {
  name: string;
  plowable: SnowSubArea | null;
  impervious: SnowSubArea | null;
  pervious: SnowSubArea | null;
  removal: SnowRemoval | null;
}

function readSubArea(row: string[], warnings?: Set<string>): SnowSubArea {
  const ctx = (field: string) => `[SNOWPACKS] "${row[0]}" ${row[1]} ${field}`;
  return {
    cmin: parseNum(row[2], 0, ctx("Cmin"), warnings),
    cmax: parseNum(row[3], 0, ctx("Cmax"), warnings),
    tbase: parseNum(row[4], 0, ctx("Tbase"), warnings),
    fwFrac: parseNum(row[5], 0, ctx("FWfrac"), warnings),
    sd0: parseNum(row[6], 0, ctx("SD0"), warnings),
    fw0: parseNum(row[7], 0, ctx("FW0"), warnings),
    snn0OrSd100: parseNum(row[8], 0, ctx("SNN0/SD100"), warnings),
  };
}

function writeSubArea(name: string, keyword: string, area: SnowSubArea): string[] {
  return [
    name,
    keyword,
    String(area.cmin),
    String(area.cmax),
    String(area.tbase),
    String(area.fwFrac),
    String(area.sd0),
    String(area.fw0),
    String(area.snn0OrSd100),
  ];
}

export function readSnowPacks(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): SnowPack[] {
  const rows = raw.get("SNOWPACKS") ?? [];
  const order: string[] = [];
  const rowsByName = new Map<string, string[][]>();
  for (const row of rows) {
    const name = row[0];
    if (!name) continue;
    if (!rowsByName.has(name)) {
      rowsByName.set(name, []);
      order.push(name);
    }
    rowsByName.get(name)!.push(row);
  }

  return order.map((name) => {
    const pack: SnowPack = {
      name,
      plowable: null,
      impervious: null,
      pervious: null,
      removal: null,
    };
    for (const row of rowsByName.get(name)!) {
      const keyword = row[1]?.toUpperCase() ?? "";
      switch (keyword) {
        case "PLOWABLE":
          pack.plowable = readSubArea(row, warnings);
          break;
        case "IMPERVIOUS":
          pack.impervious = readSubArea(row, warnings);
          break;
        case "PERVIOUS":
          pack.pervious = readSubArea(row, warnings);
          break;
        case "REMOVAL": {
          const ctx = (field: string) => `[SNOWPACKS] "${row[0]}" REMOVAL ${field}`;
          pack.removal = {
            sdPlow: parseNum(row[2], 0, ctx("SDplow"), warnings),
            fout: parseNum(row[3], 0, ctx("Fout"), warnings),
            fimperv: parseNum(row[4], 0, ctx("Fimperv"), warnings),
            fperv: parseNum(row[5], 0, ctx("Fperv"), warnings),
            fimelt: parseNum(row[6], 0, ctx("Fimelt"), warnings),
            fsubcatch: parseNum(row[7], 0, ctx("Fsubcatch"), warnings),
            toSubcatch: row[8] ?? "",
          };
          break;
        }
      }
    }
    return pack;
  });
}

export function writeSnowPacks(
  raw: ReadonlyMap<string, string[][]>,
  packs: SnowPack[]
): Map<string, string[][]> {
  const rows: string[][] = [];
  for (const p of packs) {
    if (p.plowable) rows.push(writeSubArea(p.name, "PLOWABLE", p.plowable));
    if (p.impervious) rows.push(writeSubArea(p.name, "IMPERVIOUS", p.impervious));
    if (p.pervious) rows.push(writeSubArea(p.name, "PERVIOUS", p.pervious));
    if (p.removal) {
      const r = p.removal;
      const row = [
        p.name,
        "REMOVAL",
        String(r.sdPlow),
        String(r.fout),
        String(r.fimperv),
        String(r.fperv),
        String(r.fimelt),
        String(r.fsubcatch),
      ];
      if (r.toSubcatch) row.push(r.toSubcatch);
      rows.push(row);
    }
  }
  return writeOrDeleteSection(raw, "SNOWPACKS", rows);
}

export function nextAvailableSnowPackName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "SNOW");
}
